<template>
  <div
    class="flex flex-col w-[266px]"
    @keydown.enter="handleEnterKey"
    tabindex="-1"
  >
    <div
      v-if="!enableViewType"
      class="flex flex-col py-3 px-2"
      :class="{ border_bottom: true }"
    >
      <a-input
        id="add_view_name"
        name="view_name"
        aria-label="View Name"
        v-model:value="localViewName"
        class="input_medium"
        placeholder="View Name"
        allow-clear
        @keydown.enter="handleEnterKey"
      />
    </div>

    <div
      class="flex flex-col p-3 gap-2 proxima_nova"
      :class="{ border_bottom: mode === 'edit' && !enableViewType }"
    >
      <template v-if="enableViewType">
        <template v-if="mode === 'create'">
          <span class="secondary_color text-[13px] leading-[17px]"
            >Add a new view</span
          >
          <div class="flex flex-col gap-2 mb-1">
            <div
              v-for="viewTypeOption in viewTypeOptions"
              :key="viewTypeOption.value"
              class="dropdown_item flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer"
              @click="handleViewTypeSelection(viewTypeOption.value)"
            >
              <ThemeImage
                :icon="viewTypeOption.icon"
                :alt="viewTypeOption.alt"
                class="size-4"
              />
              <span class="text-sm">{{ viewTypeOption.label }}</span>
            </div>
          </div>
        </template>

        <template v-else-if="mode === 'edit'">
          <div
            class="dropdown_item flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer"
            @click="handleEditView"
          >
            <ThemeImage
              class="size-4"
              :icon="imageNames.EDIT"
              alt="edit_view"
            />
            <span class="text-sm">Edit View</span>
          </div>
          <div
            class="dropdown_item flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer"
            @click="$emit('delete-view')"
          >
            <ThemeImage
              class="size-4"
              :icon="imageNames.DELETE_GREY"
              alt="delete_view"
            />
            <span class="text-sm">Delete View</span>
          </div>
        </template>
      </template>

      <template v-else>
        <ViewPermissionsPanel
          v-model:access-level="localAccessLevel"
          v-model:selected-members="selectedMembersLocal"
          :access-options-by-tab="accessOptionsByTab"
          :current-user-as-non-removable="currentUserAsNonRemovable"
          :enable-auto-select-current-user="shouldEnableAutoSelect"
          :selection-by-tab="selectionByTab"
          @selection-change="handleSelectionChange"
        />
        <div class="border_top pb-1">
          <a-button class="primary_medium w-full" @click="saveView">Done</a-button>
        </div>
      </template>
    </div>

    <div class="flex flex-col p-2" v-if="mode === 'edit' && !enableViewType">
      <div
        class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg cursor-pointer"
        @click="$emit('delete-view')"
      >
        <ThemeImage
          class="size-4"
          :icon="imageNames.DELETE_GREY"
          alt="dropdown_icons"
        />
        <span class="text-sm">Delete View</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, defineExpose } from "vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import ViewPermissionsPanel from "@/src/components/view_components/ViewPermissionsPanel.vue";
import eventBus from "@/src/utils/eventBus.js";
import {
  accessLevels,
  sharingScopes,
  viewTypeOptions,
} from "@/src/constants/viewConstants";

const props = defineProps({
  viewName: { type: String, default: "" },
  accessLevel: { type: String, default: "everyone" },
  accessOptionsByTab: {
    type: Object,
    default: () => ({ Users: [], Teams: [] }),
  },
  selectedMembers: { type: Array, default: () => [] },
  mode: { type: String, default: "create" },
  currentView: { type: Object, default: null },
  sharingDetails: { type: Object, default: null },
  enableViewType: { type: Boolean, default: false },
  entitySlug: { type: String, default: "" },
});

const emit = defineEmits([
  "update:viewName",
  "update:accessLevel",
  "update:selectedMembers",
  "delete-view",
  "edit-view",
  "save-view",
  "update-view",
  "close-dropdown",
  "view-type-selected",
]);

const localViewName = ref(props.viewName || "");
const localAccessLevel = ref(props.accessLevel || accessLevels.EVERYONE);
const selectedMembersLocal = ref([...props.selectedMembers]);
const selectionByTab = ref({ users: [], teams: [] });

const hasBeenSaved = ref(false);
const isInternalUpdate = ref(false);

const handleViewTypeSelection = (viewType) => {
  emit("view-type-selected", viewType);
  emit("close-dropdown");

  eventBus.emit("open-view-create-mode", viewType);
};

const handleEditView = () => {
  emit("edit-view");
  emit("close-dropdown");
};

const initializeFormData = () => {
  if (props.mode === "edit" && props.currentView && !props.enableViewType) {
    localViewName.value = props.currentView.name || "";

    if (props.currentView.sharing_scope === sharingScopes.WORKSPACE) {
      localAccessLevel.value = accessLevels.EVERYONE;
    } else if (
      props.currentView.sharing_scope === sharingScopes.TEAM_AND_USERS
    ) {
      localAccessLevel.value = accessLevels.TEAM;

      const userIds = props.currentView.user_ids || [];
      const teamIds = props.currentView.team_ids || [];
      if (userIds.length > 0 || teamIds.length > 0) {
        selectionByTab.value = {
          users: [...userIds],
          teams: [...teamIds],
        };
        selectedMembersLocal.value = [...userIds, ...teamIds];
      }
    } else {
      localAccessLevel.value = accessLevels.PRIVATE;
    }

    hasBeenSaved.value = false;
  }
};

const populateSelectedMembersFromSharingDetails = (sharingDetails) => {
  if (!sharingDetails || !sharingDetails.sharing_info) {
    return;
  }

  const sharingInfo = sharingDetails.sharing_info;

  let userIds = sharingInfo.user_ids || [];
  const teamIds = sharingInfo.team_ids || [];

  const currentUserIdRaw = localStorage.getItem("userId");
  const currentUserId = currentUserIdRaw ? parseInt(currentUserIdRaw) : null;
  if (
    props.mode === "edit" &&
    currentUserId &&
    !userIds.some((id) => String(id) === String(currentUserId))
  ) {
    userIds = [...userIds, currentUserId];
  }

  selectionByTab.value = {
    users: [...userIds],
    teams: [...teamIds],
  };

  const allSelectedIds = [...userIds, ...teamIds];
  selectedMembersLocal.value = allSelectedIds;
};

initializeFormData();

const handleSelectionChange = (data) => {
  if (data.byTab) {
    selectionByTab.value = data.byTab;
  }
};

const handleEnterKey = (event) => {
  const isFromMainInput =
    event.target.id === "add_view_name" ||
    event.target.closest("#add_view_name") ||
    event.target === event.currentTarget;

  if (isFromMainInput && localViewName.value && localViewName.value.trim()) {
    event.preventDefault();
    event.stopPropagation();
    saveView();
  }
};

const saveView = () => {
  if (hasBeenSaved.value) {
    return;
  }

  if (!localViewName.value || !localViewName.value.trim()) {
    return;
  }

  hasBeenSaved.value = true;

  let sharingScope = sharingScopes.PERSONAL;
  let teamIds = [];
  let userIds = [];

  if (localAccessLevel.value === accessLevels.EVERYONE) {
    sharingScope = sharingScopes.WORKSPACE;
  } else if (localAccessLevel.value === accessLevels.TEAM) {
    sharingScope = sharingScopes.TEAM_AND_USERS;

    userIds = selectionByTab.value.users || [];
    teamIds = selectionByTab.value.teams || [];

    const currentUserId = localStorage.getItem("userId");
    if (currentUserId && !userIds.includes(parseInt(currentUserId))) {
      userIds.push(parseInt(currentUserId));
    }
  }

  const saveData = {
    name: localViewName.value.trim(),
    sharing_scope: sharingScope,
    team_ids: teamIds,
    user_ids: userIds,
    access_level: localAccessLevel.value,
  };

  if (props.mode === "edit" && props.currentView) {
    const currentView = props.currentView;
    const currentSharingDetails = props.sharingDetails?.sharing_info;

    const hasNameChanged = currentView.name !== saveData.name;
    const hasSharingScopeChanged =
      currentView.sharing_scope !== saveData.sharing_scope;

    const currentUserIds = currentSharingDetails?.user_ids || [];
    const currentTeamIds = currentSharingDetails?.team_ids || [];

    const hasUserIdsChanged =
      userIds.length !== currentUserIds.length ||
      !userIds.every((id) => currentUserIds.includes(id));

    const hasTeamIdsChanged =
      teamIds.length !== currentTeamIds.length ||
      !teamIds.every((id) => currentTeamIds.includes(id));

    if (
      !hasNameChanged &&
      !hasSharingScopeChanged &&
      !hasUserIdsChanged &&
      !hasTeamIdsChanged
    ) {
      emit("close-dropdown");
      return;
    }
  }

  if (props.mode === "edit") {
    emit("update-view", saveData);
  } else {
    emit("save-view", saveData);
  }

  emit("close-dropdown");
};

const currentUserAsNonRemovable = computed(() => {
  const currentUserId = localStorage.getItem("userId");
  return currentUserId ? [parseInt(currentUserId)] : [];
});

const shouldEnableAutoSelect = computed(() => {
  const isEditMode = props.mode === "edit";

  if (!isEditMode) return true;

  return localAccessLevel.value === accessLevels.TEAM;
});

watch(
  () => props.viewName,
  (val) => {
    if (isInternalUpdate.value) {
      isInternalUpdate.value = false;
      return;
    }
    localViewName.value = val || "";
    hasBeenSaved.value = false;
  },
);

watch(
  () => props.accessLevel,
  (val) => {
    if (isInternalUpdate.value) {
      isInternalUpdate.value = false;
      return;
    }
    localAccessLevel.value = val || accessLevels.EVERYONE;
  },
);

watch(
  () => props.selectedMembers,
  (val) => {
    if (isInternalUpdate.value) {
      isInternalUpdate.value = false;
      return;
    }
    selectedMembersLocal.value = Array.isArray(val) ? [...val] : [];
  },
  { deep: true },
);

watch(localViewName, (val) => {
  isInternalUpdate.value = true;
  hasBeenSaved.value = false;
  emit("update:viewName", val);
});

watch(localAccessLevel, (val) => {
  isInternalUpdate.value = true;
  hasBeenSaved.value = false;
  emit("update:accessLevel", val);
});

watch(
  selectedMembersLocal,
  (val) => {
    isInternalUpdate.value = true;
    hasBeenSaved.value = false;
    emit("update:selectedMembers", Array.isArray(val) ? [...val] : []);
  },
  { deep: true },
);

watch(
  () => props.currentView,
  () => {
    if (props.mode === "edit") {
      initializeFormData();
    }
  },
  { deep: true },
);

watch(
  () => props.sharingDetails,
  (newSharingDetails) => {
    if (props.mode === "edit" && newSharingDetails) {
      populateSelectedMembersFromSharingDetails(newSharingDetails);
    }
  },
  { deep: true },
);

watch(
  () => localAccessLevel.value,
  (newVal, oldVal) => {
    if (
      props.mode === "edit" &&
      oldVal !== accessLevels.TEAM &&
      newVal === accessLevels.TEAM
    ) {
      selectionByTab.value = { users: [], teams: [] };
      selectedMembersLocal.value = [];
    }
  },
);

defineExpose({
  saveView,
});
</script>

<style scoped>
:deep(.radio_group_start .ant-radio-wrapper .ant-radio) {
  align-self: flex-start;
  margin-top: 4px;
}
</style>
